/**
 * Product builder — variant option price deltas (pure logic + optional DOM apply).
 */
(function (global) {
  'use strict';

  function normalizeVariantPriceCents(price) {
    if (typeof price === 'number' && isFinite(price)) return Math.round(price);
    if (typeof price === 'string') {
      var clean = price.replace(/[^0-9.]/g, '');
      var val = parseFloat(clean);
      if (isNaN(val)) return null;
      if (price.indexOf('.') !== -1) return Math.round(val * 100);
      return Math.round(val);
    }
    return null;
  }

  function findOptionIndex(options, optionName) {
    if (!options || !optionName) return -1;
    for (var i = 0; i < options.length; i++) {
      if (options[i] && options[i].name === optionName) return i;
    }
    return -1;
  }

  function buildPriorSelections(options, selections, touched, currentOptionName) {
    var prior = {};
    (options || []).forEach(function (opt) {
      if (!opt || opt.name === 'Title' || opt.name === currentOptionName) return;
      if (touched && touched[opt.name] && selections && selections[opt.name]) {
        prior[opt.name] = selections[opt.name];
      }
    });
    return prior;
  }

  function variantMatches(variant, options, priorSelections, optionIndex, optionValue) {
    if (!variant || variant.available === false) return false;
    if (!variant.options || variant.options[optionIndex] !== optionValue) return false;
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      if (!opt || opt.name === 'Title') continue;
      var required = priorSelections[opt.name];
      if (!required) continue;
      if (variant.options[i] !== required) return false;
    }
    return true;
  }

  /**
   * Lowest matching variant price per option value on this step.
   * @returns {{ show: boolean, prices: Object.<string, number|null>, baselineCents?: number }}
   */
  function computeStepOptionPrices(params) {
    var variants = params.variants || [];
    var options = params.options || [];
    var optionName = params.optionName;
    var optionValues = params.optionValues || [];
    var optionIndex = findOptionIndex(options, optionName);
    if (optionIndex < 0 || !optionValues.length) {
      return { show: false, prices: {} };
    }

    var priorSelections = buildPriorSelections(
      options,
      params.selections || {},
      params.touched || {},
      optionName
    );

    var pricesByValue = {};
    optionValues.forEach(function (value) {
      var minPrice = null;
      variants.forEach(function (variant) {
        if (!variantMatches(variant, options, priorSelections, optionIndex, value)) return;
        var cents = normalizeVariantPriceCents(variant.price);
        if (cents == null) return;
        if (minPrice === null || cents < minPrice) minPrice = cents;
      });
      pricesByValue[value] = minPrice;
    });

    var validPrices = Object.keys(pricesByValue)
      .map(function (key) { return pricesByValue[key]; })
      .filter(function (p) { return p !== null; });

    if (!validPrices.length) return { show: false, prices: {} };

    var minBaseline = Math.min.apply(null, validPrices);
    var maxBaseline = Math.max.apply(null, validPrices);
    if (minBaseline === maxBaseline) return { show: false, prices: {}, baselineCents: minBaseline };

    return { show: true, prices: pricesByValue, baselineCents: minBaseline };
  }

  /** @deprecated — use computeStepOptionPrices; kept for tests / backwards compat */
  function computeStepPriceDeltas(params) {
    var result = computeStepOptionPrices(params);
    if (!result.show) return { show: false, deltas: {}, baselineCents: result.baselineCents };
    var baseline = result.baselineCents;
    var deltas = {};
    Object.keys(result.prices).forEach(function (value) {
      var price = result.prices[value];
      if (price === null) return;
      deltas[value] = price - baseline;
    });
    return { show: true, deltas: deltas, baselineCents: baseline };
  }

  function formatOptionPrice(priceCents, formatMoney) {
    if (priceCents == null) return '';
    var formatted = typeof formatMoney === 'function' ? formatMoney(priceCents) : String(priceCents);
    return formatted ? '\u00b7 ' + formatted : '';
  }

  function formatPriceDelta(deltaCents, formatMoney) {
    if (!deltaCents || deltaCents <= 0) return '';
    var formatted = typeof formatMoney === 'function' ? formatMoney(deltaCents) : String(deltaCents);
    if (!formatted) return '';
    if (formatted.charAt(0) === '+' || formatted.charAt(0) === '-') return formatted;
    return '+' + formatted;
  }

  function formatOptionMetaLine(priceText, noteText) {
    if (global.PBOptionLabel && global.PBOptionLabel.formatOptionMetaLine) {
      return global.PBOptionLabel.formatOptionMetaLine(priceText, noteText);
    }
    var price = (priceText || '').trim();
    var note = (noteText || '').trim();
    if (price && note) return price + ' \u00b7 ' + note;
    return price || note || '';
  }

  /** @returns {{ priceCents: number, compareAtCents: number }} */
  function pricingFromVariant(variant, itemFallback) {
    var priceCents = variant ? normalizeVariantPriceCents(variant.price) : null;
    var compareRaw = variant
      ? (variant.compare_at_price != null ? variant.compare_at_price : variant.compare_at)
      : null;
    var compareAtCents = variant ? normalizeVariantPriceCents(compareRaw) : null;

    if (priceCents == null && itemFallback) {
      priceCents = normalizeVariantPriceCents(itemFallback.price);
    }
    if (compareAtCents == null && itemFallback) {
      compareAtCents = normalizeVariantPriceCents(itemFallback.compare_at);
    }
    if (priceCents == null) priceCents = 0;
    if (compareAtCents == null || compareAtCents <= priceCents) compareAtCents = priceCents;
    return { priceCents: priceCents, compareAtCents: compareAtCents };
  }

  /**
   * @param {Array<{ priceCents: number, compareAtCents: number }>} linePricings
   * @param {function} calcBundleDiscountCents - (totalSelling, discountVal, discountType) => cents
   */
  function calcBundleDisplayTotals(linePricings, discountVal, discountType, calcBundleDiscountCents) {
    var totalSelling = 0;
    var totalValue = 0;
    (linePricings || []).forEach(function (p) {
      var price = p && p.priceCents != null ? p.priceCents : 0;
      var compare = p && p.compareAtCents != null ? p.compareAtCents : price;
      totalSelling += price;
      totalValue += compare > price ? compare : price;
    });
    var discountAmount =
      typeof calcBundleDiscountCents === 'function'
        ? calcBundleDiscountCents(totalSelling, discountVal, discountType)
        : 0;
    var finalPrice = totalSelling - discountAmount;
    var totalSaved = totalValue > finalPrice ? totalValue - finalPrice : 0;
    return {
      totalSellingCents: totalSelling,
      totalValueCents: totalValue,
      finalBundlePriceCents: finalPrice,
      totalSavedCents: totalSaved,
      discountAmountCents: discountAmount
    };
  }

  function parseOptionLabelFallback(text) {
    var raw = typeof text === 'string' ? text.replace(/\s+/g, ' ').trim() : '';
    if (!raw) return { primary: '', secondary: '' };
    var match = raw.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (!match) return { primary: raw, secondary: '' };
    return { primary: match[1].trim(), secondary: match[2].trim() };
  }

  function parseBtnLabel(btn) {
    var parse =
      global.PBOptionLabel && global.PBOptionLabel.parseOptionLabel
        ? global.PBOptionLabel.parseOptionLabel
        : parseOptionLabelFallback;
    return parse(getVariantBtnLabelText(btn));
  }

  function getVariantBtnLabelText(btn) {
    var fromData = (btn.dataset.pbTemplate || btn.dataset.value || '').replace(/\s+/g, ' ').trim();
    if (fromData) return fromData;
    return (btn.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function flattenVariantBtn(btn, primary) {
    if (global.PBOptionLabel && global.PBOptionLabel.flattenButtonToPrimary) {
      global.PBOptionLabel.flattenButtonToPrimary(btn, primary);
      return;
    }
    btn.classList.remove('pb-variant-has-pricing');
    btn.textContent = primary;
  }

  function ensureOptionCell(btn) {
    if (global.PBOptionLabel && global.PBOptionLabel.ensureCellWrap) {
      return global.PBOptionLabel.ensureCellWrap(btn);
    }
    if (btn.parentElement && btn.parentElement.classList.contains('pb-option-cell')) {
      return btn.parentElement;
    }
    var cell = document.createElement('div');
    cell.className = 'pb-option-cell';
    btn.parentElement.insertBefore(cell, btn);
    cell.appendChild(btn);
    return cell;
  }

  function setOptionMetaTag(cell, text) {
    if (global.PBOptionLabel && global.PBOptionLabel.ensureTag) {
      return global.PBOptionLabel.ensureTag(cell, text);
    }
    var tag = cell.querySelector(':scope > .pb-option-tag');
    if (!tag) {
      tag = document.createElement('span');
      tag.className = 'pb-option-tag';
      cell.insertBefore(tag, cell.firstChild);
    }
    tag.textContent = text || '';
    if (!text) tag.setAttribute('aria-hidden', 'true');
    else tag.removeAttribute('aria-hidden');
    return tag;
  }

  function buildAriaLabel(primary, deltaText, secondary) {
    var parts = [primary];
    if (deltaText) parts.push(deltaText);
    if (secondary) parts.push(secondary);
    return parts.filter(Boolean).join(', ');
  }

  /**
   * @param {Element} container
   * @param {object} ctx - builder context (variants, options, selections, touched)
   * @param {function} formatMoney - cents -> formatted string
   */
  function applyVariantPriceBadges(container, ctx, formatMoney) {
    if (!container || !ctx) return;
    var tracks = container.querySelectorAll('.variant-slider-track');
    tracks.forEach(function (track) {
      var optionName = track.dataset.optionName || track.dataset.option;
      if (!optionName || optionName === 'Title') return;

      var buttons = Array.from(track.querySelectorAll('.variant-btn'));
      var optionValues = buttons.map(function (btn) { return btn.dataset.value; }).filter(Boolean);

      var result = computeStepPriceDeltas({
        variants: ctx.variants || [],
        options: ctx.options || [],
        selections: ctx.selections || {},
        touched: ctx.touched || {},
        optionName: optionName,
        optionValues: optionValues
      });

      if (!result.show) {
        buttons.forEach(function (btn) {
          delete btn.dataset.pbPriceDelta;
          flattenVariantBtn(btn, parseBtnLabel(btn).primary);
        });
        if (global.PBOptionLabel && global.PBOptionLabel.enhanceGroup) {
          global.PBOptionLabel.enhanceGroup(track);
        }
        return;
      }

      track.setAttribute('data-pb-has-sublabels', 'true');
      buttons.forEach(function (btn) {
        var value = btn.dataset.value;
        var deltaCents = result.deltas[value];
        var parsed = parseBtnLabel(btn);
        var deltaText = formatPriceDelta(deltaCents, formatMoney);
        btn.dataset.pbPriceDelta = deltaText;
        var metaLine = formatOptionMetaLine(deltaText, parsed.secondary);
        var cell = ensureOptionCell(btn);
        setOptionMetaTag(cell, metaLine);
        flattenVariantBtn(btn, parsed.primary);
        btn.setAttribute('aria-label', buildAriaLabel(parsed.primary, deltaText, parsed.secondary));
      });

      if (global.PBOptionLabel && global.PBOptionLabel.syncMetaRows) {
        global.PBOptionLabel.syncMetaRows(track);
      }
    });
  }

  var api = {
    normalizeVariantPriceCents: normalizeVariantPriceCents,
    computeStepOptionPrices: computeStepOptionPrices,
    computeStepPriceDeltas: computeStepPriceDeltas,
    formatOptionPrice: formatOptionPrice,
    formatPriceDelta: formatPriceDelta,
    formatOptionMetaLine: formatOptionMetaLine,
    pricingFromVariant: pricingFromVariant,
    calcBundleDisplayTotals: calcBundleDisplayTotals,
    applyVariantPriceBadges: applyVariantPriceBadges
  };

  global.PBVariantPricing = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
